/**
 * Dashboard drilldowns — a clicked breakdown value on one widget becomes a
 * filter for the other widgets on the same dataset.
 *
 * Drilldowns are a layer of their own, separate from the dashboard's build
 * filters and the viewer's user filters: they are never persisted, never
 * touch the sidebar, and AND with everything else. One drilldown per field;
 * selecting a second value on the same field replaces the first, and
 * re-selecting the current value clears it.
 */

import { generateColumnLabel } from '../label-generation';
import type { DateGroupingInterval } from '../aggregation-types';
import type { FilterSummaryLine } from './filter-summary';
import type { WidgetConfig } from './widget-config-types';

export type DrilldownSelection =
  | { kind: 'eq'; value: string }
  | { kind: 'range'; gte: string; lt: string }
  | { kind: 'ranges'; ranges: DateRange[] }
  | { kind: 'in'; values: string[] };

/** Half-open date range (`gte` ≤ value < `lt`), ISO dates: exact whatever the engine does with date-only upper bounds. */
export interface DateRange {
  gte: string;
  lt: string;
}

export interface Drilldown {
  /** The widget whose value was clicked — it never filters itself. */
  sourceWidgetId: string;
  /** Only widgets on this dataset receive the filter; field keys don't cross datasets. */
  dataset: string;
  fieldKey: string;
  selection: DrilldownSelection;
  /** Display text for the clicked value, as the chart showed it. */
  valueLabel: string;
  /** Display label of the field; falls back to the generated column label. */
  fieldLabel?: string;
  /** Raw key of the clicked category, so the source widget can highlight it. */
  categoryKey?: string;
}

/** One choice in the pick-a-dimension menu; applying it toggles every drilldown listed. */
export interface DrilldownOption {
  label: string;
  drilldowns: Drilldown[];
}

interface OptionContext {
  widget: WidgetConfig;
  /** Display label for a field key; the generated column label when unknown. */
  fieldLabelFor: (fieldKey: string) => string | undefined;
  /** Whether the field is a date; the aggregation buckets those by year when no interval is stored. */
  isDateField: (fieldKey: string) => boolean;
}

/** The interval the aggregation actually used: the stored one, else year for a date field. */
export function effectiveDateInterval(
  stored: DateGroupingInterval | undefined,
  isDateField: boolean
): DateGroupingInterval | undefined {
  return stored ?? (isDateField ? 'year' : undefined);
}

function optionFor(
  ctx: OptionContext,
  fieldKey: string,
  category: CategoryKey,
  valueLabel: string,
  dateInterval?: DateGroupingInterval
): DrilldownOption | undefined {
  const selection = selectionForCategory(
    category,
    effectiveDateInterval(dateInterval, ctx.isDateField(fieldKey))
  );
  if (!selection) return undefined;
  const fieldLabel = ctx.fieldLabelFor(fieldKey) ?? generateColumnLabel(fieldKey);
  return {
    label: `${fieldLabel}: ${valueLabel}`,
    drilldowns: [
      {
        sourceWidgetId: ctx.widget.id,
        dataset: ctx.widget.dataset,
        fieldKey,
        selection,
        valueLabel,
        fieldLabel,
        categoryKey: category.key,
      },
    ],
  };
}

/** Merge single-field options into one "Both"/"All" choice; none when fewer than two. */
function combinedOption(options: DrilldownOption[], label: string): DrilldownOption | undefined {
  if (options.length < 2) return undefined;
  return { label, drilldowns: options.flatMap((o) => o.drilldowns) };
}

/**
 * The choices a click on a bar chart offers: the x value, the group value
 * (when the clicked series is a group split), or both. A click on a column's
 * empty space or on a measure series offers the x value alone.
 */
export function barDrilldownOptions(
  ctx: OptionContext,
  data: { keys: string[]; labels: string[]; series: Array<{ label: string; key?: string }> },
  hit: { index: number; seriesIndex: number | null }
): DrilldownOption[] {
  const { widget } = ctx;
  const options: DrilldownOption[] = [];
  const xKey = data.keys[hit.index];
  if (widget.xDimension && xKey !== undefined) {
    const option = optionFor(
      ctx,
      widget.xDimension.fieldKey,
      { key: xKey },
      data.labels[hit.index] ?? xKey,
      widget.xDimension.dateInterval
    );
    if (option) options.push(option);
  }
  const series = hit.seriesIndex === null ? undefined : data.series[hit.seriesIndex];
  if (widget.groupDimension && series?.key !== undefined) {
    const option = optionFor(
      ctx,
      widget.groupDimension.fieldKey,
      { key: series.key },
      series.label
    );
    if (option) options.push(option);
  }
  const both = combinedOption(options, 'Both');
  return both ? [...options, both] : options;
}

/**
 * The choices a click on an aggregated table row offers: one per group-by
 * column (its value in that row), plus "All" when there are several. Rows
 * come from the flattened aggregation, keyed by field key.
 * @param valueLabelFor display text for a raw group-by value
 */
export function rowDrilldownOptions(
  ctx: OptionContext,
  row: Record<string, unknown>,
  valueLabelFor: (fieldKey: string, raw: string) => string
): DrilldownOption[] {
  const groupBy = ctx.widget.table?.groupBy;
  const options: DrilldownOption[] = [];
  for (const fieldKey of groupBy?.selectedFields ?? []) {
    const raw = row[fieldKey];
    if (raw === undefined || raw === null || raw === '') continue;
    const key = String(raw);
    const option = optionFor(
      ctx,
      fieldKey,
      { key },
      valueLabelFor(fieldKey, key),
      groupBy?.dateGroupingIntervals?.[fieldKey]
    );
    if (option) options.push(option);
  }
  const all = combinedOption(options, 'All of these');
  return all ? [...options, all] : options;
}

/** Identity of a drilldown: one per field per dataset. */
export function drilldownKey(d: Pick<Drilldown, 'dataset' | 'fieldKey'>): string {
  return `${d.dataset}|${d.fieldKey}`;
}

function sameSelection(a: DrilldownSelection, b: DrilldownSelection): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'eq' && b.kind === 'eq') return a.value === b.value;
  if (a.kind === 'range' && b.kind === 'range') return a.gte === b.gte && a.lt === b.lt;
  if (a.kind === 'ranges' && b.kind === 'ranges') {
    const key = (r: DateRange) => `${r.gte}|${r.lt}`;
    const bSet = new Set(b.ranges.map(key));
    return a.ranges.length === b.ranges.length && a.ranges.every((r) => bSet.has(key(r)));
  }
  if (a.kind === 'in' && b.kind === 'in') {
    const bSet = new Set(b.values);
    return a.values.length === b.values.length && a.values.every((v) => bSet.has(v));
  }
  return false;
}

/**
 * Apply a click: a new field appends, the same field with a different value
 * replaces in place, and the same field with the same value clears it.
 */
export function toggleDrilldown(list: Drilldown[], next: Drilldown): Drilldown[] {
  const key = drilldownKey(next);
  const current = list.find((d) => drilldownKey(d) === key);
  if (!current) return [...list, next];
  if (sameSelection(current.selection, next.selection)) {
    return list.filter((d) => d !== current);
  }
  return list.map((d) => (d === current ? next : d));
}

/** Apply without the toggle: a new field appends, an existing one is replaced, never cleared. */
export function setDrilldown(list: Drilldown[], next: Drilldown): Drilldown[] {
  const key = drilldownKey(next);
  const current = list.find((d) => drilldownKey(d) === key);
  if (!current) return [...list, next];
  return list.map((d) => (d === current ? next : d));
}

/** How a click applies: `toggle` clears a re-selected value, `set` never clears (combined picks). */
export type DrilldownMode = 'toggle' | 'set';

/** Drop the drilldown with this `drilldownKey`; unknown keys leave the list unchanged. */
export function removeDrilldown(list: Drilldown[], key: string): Drilldown[] {
  return list.filter((d) => drilldownKey(d) !== key);
}

/** The drilldowns a widget receives: same dataset, not its own. */
export function drilldownsFor(
  list: Drilldown[] | undefined,
  widget: { id: string; dataset: string }
): Drilldown[] {
  return (list ?? []).filter((d) => d.dataset === widget.dataset && d.sourceWidgetId !== widget.id);
}

/**
 * Encode drilldowns as suffix-keyed `filterValues` entries for the shared clause builder.
 * Keep the map apart from the user filters so a same-field drilldown ANDs instead of overwriting.
 */
export function drilldownFilterValues(list: Drilldown[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const d of list) {
    const s = d.selection;
    if (s.kind === 'eq') values[d.fieldKey] = s.value;
    else if (s.kind === 'range') {
      values[`${d.fieldKey}__gte`] = s.gte;
      values[`${d.fieldKey}__lt`] = s.lt;
    } else if (s.kind === 'ranges') values[`${d.fieldKey}__ranges`] = s.ranges;
    else values[`${d.fieldKey}__in`] = s.values;
  }
  return values;
}

/** Chip/badge line for one drilldown. */
export function summarizeDrilldown(d: Drilldown): FilterSummaryLine {
  return {
    label: d.fieldLabel ?? generateColumnLabel(d.fieldKey),
    text: `is: ${d.valueLabel}`,
  };
}

/** Chip/badge lines, one per drilldown. */
export function summarizeDrilldowns(list: Drilldown[]): FilterSummaryLine[] {
  return list.map(summarizeDrilldown);
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const isoDate = (d: Date) =>
  `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const utc = (y: number, m: number, d: number) => new Date(Date.UTC(y, m, d));

/**
 * Turn a date-histogram bucket key into the half-open date range it covers.
 * Bucket keys follow the `key_as_string` format the aggregation query
 * requests per interval: `yyyy`, `yyyy-QQQ` (e.g. 2026-Q1), `yyyy-MM`, and
 * `yyyy-MM-dd` for both week (the bucket's first day) and day.
 * @returns undefined when the key does not parse for the interval
 */
export function dateBucketRange(
  bucket: string,
  interval: DateGroupingInterval
): DateRange | undefined {
  const num = (s: string | undefined) => (s === undefined ? NaN : Number(s));
  switch (interval) {
    case 'year': {
      const m = /^(\d{4})$/.exec(bucket);
      if (!m) return undefined;
      const y = num(m[1]);
      return { gte: isoDate(utc(y, 0, 1)), lt: isoDate(utc(y + 1, 0, 1)) };
    }
    case 'quarter': {
      const m = /^(\d{4})-Q([1-4])$/.exec(bucket);
      if (!m) return undefined;
      const y = num(m[1]);
      const startMonth = (num(m[2]) - 1) * 3;
      return { gte: isoDate(utc(y, startMonth, 1)), lt: isoDate(utc(y, startMonth + 3, 1)) };
    }
    case 'month': {
      const m = /^(\d{4})-(\d{2})$/.exec(bucket);
      if (!m) return undefined;
      const y = num(m[1]);
      const month = num(m[2]) - 1;
      return { gte: isoDate(utc(y, month, 1)), lt: isoDate(utc(y, month + 1, 1)) };
    }
    case 'week':
    case 'day': {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket);
      if (!m) return undefined;
      const start = utc(num(m[1]), num(m[2]) - 1, num(m[3]));
      const days = interval === 'week' ? 7 : 1;
      return { gte: isoDate(start), lt: isoDate(utc(num(m[1]), num(m[2]) - 1, num(m[3]) + days)) };
    }
    default:
      return undefined;
  }
}

/** Raw identity a chart point carries for its category (see widget-data-adapter). A type alias, not an interface, so it fits tio-ui's `metadata` record. */
export type CategoryKey = {
  key?: string;
  /** Set on a synthetic "Other" bucket: the raw keys of the tail categories it sums. */
  keys?: string[];
};

/**
 * The selection a clicked category stands for: a date bucket becomes its
 * range, an "Other" bucket is the exact set of tail categories it summed,
 * anything else is an exact match.
 * @returns undefined when the category carries nothing filterable
 */
export function selectionForCategory(
  category: CategoryKey | undefined,
  dateInterval?: DateGroupingInterval
): DrilldownSelection | undefined {
  if (!category) return undefined;
  if (category.keys) {
    if (dateInterval) {
      // Date tail buckets are periods, not values: match any of their ranges.
      const ranges = category.keys.flatMap((k) => dateBucketRange(k, dateInterval) ?? []);
      return ranges.length ? { kind: 'ranges', ranges } : undefined;
    }
    return category.keys.length ? { kind: 'in', values: category.keys } : undefined;
  }
  if (category.key === undefined || category.key === '') return undefined;
  if (dateInterval) {
    const range = dateBucketRange(category.key, dateInterval);
    return range ? { kind: 'range', ...range } : undefined;
  }
  return { kind: 'eq', value: category.key };
}
